import React, { useCallback, useEffect, useState } from 'react';
import { Clock3, LogOut, MonitorSmartphone, RefreshCw, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const formatDate = (value) => {
  if (!value) return 'Não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short', timeStyle: 'short',
  }).format(new Date(value));
};

export default function AccountSecurityPage() {
  const { logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [legacySession, setLegacySession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState('');
  const [message, setMessage] = useState(null);
  const [twoFactor, setTwoFactor] = useState({ enabled: false, recovery_codes_remaining: 0 });
  const [setup, setSetup] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [showDisable, setShowDisable] = useState(false);

  const load = useCallback(async (preserveMessage = false) => {
    try {
      setLoading(true);
      if (!preserveMessage) setMessage(null);
      const [sessionsResponse, twoFactorResponse] = await Promise.all([
        api.get('/auth/sessions'), api.get('/auth/2fa/status'),
      ]);
      setSessions(sessionsResponse.data?.items || []);
      setLegacySession(Boolean(sessionsResponse.data?.legacy_session));
      setTwoFactor(twoFactorResponse.data || { enabled: false, recovery_codes_remaining: 0 });
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível carregar os dispositivos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (session) => {
    const text = session.current
      ? 'Encerrar a sessão deste dispositivo? Você precisará entrar novamente.'
      : `Encerrar o acesso em ${session.device_label}?`;
    if (!window.confirm(text)) return;
    try {
      setWorkingId(session.id);
      const response = await api.delete(`/auth/sessions/${session.id}`);
      if (response.data?.current_session_revoked) {
        logout(false);
        window.location.assign('/login');
        return;
      }
      setMessage({ type: 'success', text: 'Sessão encerrada com sucesso.' });
      await load(true);
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível encerrar a sessão.' });
    } finally {
      setWorkingId('');
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm('Encerrar o acesso em todos os outros dispositivos?')) return;
    try {
      setWorkingId('others');
      const response = await api.post('/auth/sessions/revoke-others');
      const count = response.data?.revoked_count || 0;
      setMessage({ type: 'success', text: count ? `${count} sessão(ões) encerrada(s).` : 'Não havia outras sessões ativas.' });
      await load(true);
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível encerrar as outras sessões.' });
    } finally {
      setWorkingId('');
    }
  };

  const otherSessions = sessions.filter((session) => !session.current).length;

  const startTwoFactorSetup = async () => {
    try {
      setWorkingId('2fa-setup');
      setMessage(null);
      const response = await api.post('/auth/2fa/setup');
      const qrCode = await QRCode.toDataURL(response.data.provisioning_uri, { width: 240, margin: 1 });
      setSetup({ ...response.data, qrCode });
      setSetupCode('');
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível iniciar a configuração.' });
    } finally { setWorkingId(''); }
  };

  const confirmTwoFactor = async () => {
    try {
      setWorkingId('2fa-confirm');
      const response = await api.post('/auth/2fa/confirm', { code: setupCode });
      setRecoveryCodes(response.data?.recovery_codes || []);
      setSetup(null);
      setSetupCode('');
      setTwoFactor({ enabled: true, recovery_codes_remaining: response.data?.recovery_codes?.length || 0 });
      setMessage({ type: 'success', text: 'Autenticação em dois fatores ativada.' });
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Código inválido.' });
    } finally { setWorkingId(''); }
  };

  const disableTwoFactor = async () => {
    if (!window.confirm('Desativar a autenticação em dois fatores desta conta?')) return;
    try {
      setWorkingId('2fa-disable');
      await api.delete('/auth/2fa', { data: disableForm });
      setTwoFactor({ enabled: false, recovery_codes_remaining: 0 });
      setShowDisable(false);
      setDisableForm({ password: '', code: '' });
      setMessage({ type: 'success', text: 'Autenticação em dois fatores desativada.' });
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível desativar o recurso.' });
    } finally { setWorkingId(''); }
  };

  return <Layout>
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-purple-600" /> Segurança da conta</h1>
          <p className="text-sm text-gray-500 mt-1">Confira onde sua conta está conectada e encerre acessos que não reconhecer.</p>
        </div>
        <button type="button" onClick={() => load()} disabled={loading} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      {message && <div className={`mb-4 rounded-lg border p-3 text-sm ${message.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>{message.text}</div>}
      {legacySession && <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">Esta sessão foi iniciada antes do controle de dispositivos. Saia e entre novamente para identificá-la nesta lista.</div>}

      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><h2 className="font-semibold text-gray-900">Dispositivos conectados</h2><p className="text-sm text-gray-500 mt-0.5">A localização é representada pelo endereço de internet usado no acesso.</p></div>
          {otherSessions > 0 && <button type="button" disabled={!!workingId} onClick={revokeOthers} className="text-sm px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Encerrar outras sessões</button>}
        </div>
        {loading ? <div className="p-10 text-center text-gray-500">Carregando dispositivos...</div> : sessions.length === 0 ? <div className="p-10 text-center text-gray-500">Nenhuma sessão identificada.</div> : <div className="divide-y">
          {sessions.map((session) => <div key={session.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${session.current ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`}><MonitorSmartphone className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex flex-wrap items-center gap-2"><p className="font-medium text-gray-900">{session.device_label || 'Dispositivo não identificado'}</p>{session.current && <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">Este dispositivo</span>}</div>
              <p className="text-sm text-gray-500 mt-1">IP: {session.ip || 'não identificado'}</p>
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" /> Última atividade: {formatDate(session.last_seen_at)} · Login: {formatDate(session.created_at)}</p>
            </div>
            <button type="button" disabled={!!workingId} onClick={() => revoke(session)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-50"><LogOut className="w-4 h-4" /> {workingId === session.id ? 'Encerrando...' : session.current ? 'Sair deste dispositivo' : 'Encerrar'}</button>
          </div>)}
        </div>}
      </div>

      <div className="bg-white border rounded-xl shadow-sm overflow-hidden mt-6">
        <div className="px-5 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><h2 className="font-semibold text-gray-900">Autenticação em dois fatores</h2><p className="text-sm text-gray-500 mt-0.5">Use um aplicativo autenticador, como Google Authenticator, Microsoft Authenticator ou outro compatível, para proteger o login além da senha.</p></div>
          <span className={`self-start sm:self-auto px-2.5 py-1 rounded-full text-xs ${twoFactor.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{twoFactor.enabled ? 'Ativa' : 'Desativada'}</span>
        </div>
        <div className="p-5">
          {!twoFactor.enabled && !setup && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"><p className="text-sm text-gray-600 text-left">Ao ativar, você lerá um QR Code com o aplicativo autenticador escolhido. Depois, cada novo login exigirá o código temporário gerado no celular.</p><button type="button" disabled={!!workingId} onClick={startTwoFactorSetup} className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50">{workingId === '2fa-setup' ? 'Preparando...' : 'Ativar proteção'}</button></div>}
          {setup && <div className="max-w-xl mx-auto text-left">
            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700 mb-4"><li>Abra o Google Authenticator, Microsoft Authenticator ou outro aplicativo compatível.</li><li>Leia o QR Code abaixo.</li><li>Digite o código de 6 dígitos para confirmar.</li></ol>
            <div className="flex justify-center"><img src={setup.qrCode} alt="QR Code para configurar autenticação em dois fatores" className="w-60 h-60 border rounded-lg" /></div>
            <p className="text-xs text-gray-500 text-center mt-3">Se não conseguir ler o QR Code, use esta chave:</p><code className="block text-center mt-1 text-sm font-mono break-all bg-gray-50 border rounded p-2">{setup.secret}</code>
            <div className="flex flex-col sm:flex-row gap-2 mt-4"><input inputMode="numeric" autoComplete="one-time-code" value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Código de 6 dígitos" className="flex-1 border rounded-lg px-3 py-2 text-center tracking-widest" /><button type="button" disabled={setupCode.length !== 6 || !!workingId} onClick={confirmTwoFactor} className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm disabled:opacity-50">{workingId === '2fa-confirm' ? 'Confirmando...' : 'Confirmar e ativar'}</button><button type="button" onClick={() => { setSetup(null); setSetupCode(''); }} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button></div>
          </div>}
          {twoFactor.enabled && !showDisable && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"><p className="text-sm text-gray-600 text-left">Proteção ativa · {twoFactor.recovery_codes_remaining} código(s) de recuperação disponível(is).</p><button type="button" onClick={() => setShowDisable(true)} className="px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm hover:bg-red-50">Desativar</button></div>}
          {twoFactor.enabled && showDisable && <div className="max-w-lg text-left"><p className="text-sm text-gray-600 mb-3">Confirme sua senha e um código do aplicativo ou de recuperação.</p><div className="grid sm:grid-cols-2 gap-2"><input type="password" value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} placeholder="Senha atual" className="border rounded-lg px-3 py-2" /><input value={disableForm.code} onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value.toUpperCase().slice(0, 20) })} placeholder="Código" className="border rounded-lg px-3 py-2" /></div><div className="flex gap-2 mt-3"><button type="button" disabled={!disableForm.password || disableForm.code.length < 6 || !!workingId} onClick={disableTwoFactor} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm disabled:opacity-50">{workingId === '2fa-disable' ? 'Desativando...' : 'Confirmar desativação'}</button><button type="button" onClick={() => setShowDisable(false)} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button></div></div>}
        </div>
      </div>

      {recoveryCodes.length > 0 && <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-5 text-left"><h2 className="font-semibold text-amber-900">Salve seus códigos de recuperação</h2><p className="text-sm text-amber-800 mt-1">Cada código funciona uma única vez. Guarde-os fora do OdontoAssist AI. Eles não serão exibidos novamente.</p><div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">{recoveryCodes.map((code) => <code key={code} className="bg-white border border-amber-200 rounded px-2 py-2 text-center text-sm">{code}</code>)}</div><button type="button" onClick={() => navigator.clipboard?.writeText(recoveryCodes.join('\n'))} className="mt-4 px-4 py-2 border border-amber-300 rounded-lg text-sm text-amber-900 hover:bg-amber-100">Copiar códigos</button><button type="button" onClick={() => setRecoveryCodes([])} className="mt-4 ml-2 px-4 py-2 bg-amber-700 text-white rounded-lg text-sm">Já salvei</button></div>}

      <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Se você não reconhecer um dispositivo, encerre a sessão e altere sua senha. A redefinição de senha desconecta todos os acessos anteriores.</div>
    </div>
  </Layout>;
}
